import { FeatureFlagMap, FeatureFlags, TableSizeEnum, TColumnsDef } from '@fabric-ui/components';
import { HeadCellType } from '@fabric-ui/components/Table';
import {
  defaultAlertsNewSortBy,
  defaultExceptionsSortBy,
  defaultIncidentThreatsSortBy,
  defaultSortBy,
  defaultSortByTickets,
  FilterIncidentEntityTypesLabelV2,
  FilterTypesLabelV2,
} from '@fabric-ui/utils';
import { CURRENT_STATUS_FIELD, NESTED_CURRENT_STATUS_FIELD } from '@shared/Tickets/utils';
import { AppName, appNames } from '@utils/appNames';
import {
  baseAlertSql,
  baseAssetSql,
  baseExceptionsSql,
  baseFindingSql,
  baseIdentitiesSql,
  baseIdentityFindingsSql,
  baseIdentitySql,
  baseIncidentSql,
  basePolicyViolationSql,
  BaseSqlParams,
  baseTenantSql,
  baseTicketSql,
  baseViolationTicketSql,
} from '@utils/baseSql';
import { GET_DISCOVERY_SETTINGS_LIST } from '@utils/configurationBannerUtils';
import { TicketsNoDataFoundDefault } from '@utils/ticketsEmptyStates/TicketsNoDataFoundDefault';
import { ConfigurationBannerProps } from '@views/EASMSettings/components/ConfigurationBanner';
import { BucketType } from '@views/Settings/TicketStatuses/statuses.utils';
import AlertsViewCharts from '@views/Tickets/EntitiesChartsWidgets/AlertsViewCharts';
import AssetsViewCharts from '@views/Tickets/EntitiesChartsWidgets/AssetsViewCharts';
import ExceptionsIdnpViewCharts from '@views/Tickets/EntitiesChartsWidgets/ExceptionsIdnpViewCharts';
import ExceptionsViewCharts from '@views/Tickets/EntitiesChartsWidgets/ExceptionsViewCharts';
import FindingsViewCharts from '@views/Tickets/EntitiesChartsWidgets/FindingsViewCharts';
import IdentitiesIdentityViewCharts from '@views/Tickets/EntitiesChartsWidgets/IdentitiesIdentityViewCharts';
import IdentityFindingsViewCharts from '@views/Tickets/EntitiesChartsWidgets/IdentityFindingsViewCharts';
import IncidentsViewCharts from '@views/Tickets/EntitiesChartsWidgets/IncidentsViewCharts';
import PolicyViolationCharts from '@views/Tickets/EntitiesChartsWidgets/PolicyViolationCharts';
import TenantsViewCharts from '@views/Tickets/EntitiesChartsWidgets/TenantsViewCharts';
import type { IEntityViewChartsProps } from '@views/Tickets/EntitiesChartsWidgets/types';
import { entitySearchFields, RISK_MASS_DIMS } from '@views/Tickets/ticket.types';
import {
  getAlertsSql,
  getAssetsSqlV2,
  getExceptionsIdnpSql,
  getExceptionsSql,
  getFindingSqlV2,
  getIdentitiesSql,
  getIdentityFindingsSql,
  getIncidentSqlV2,
  getPolicyViolationSql,
  getTenantsSql,
  getTicketSqlV2,
  getViolationTicketsSql,
  TicketDetailSqlParams,
} from '@views/Tickets/ticketInfo.utils';
import { VulCountBySeveritySummaryConfig } from '@views/Tickets/VulcountBySeveritySummary/utils';
import { PagePathsValue, PermissionToResourceType } from '../context/AvContext.types';
import { SearchContextKeys } from '../context/SearchContext.types';
import {
  APP_PATHS,
  AppPathsValue,
  FeatureFlagValue,
  PAGE_PATHS,
  Permission,
  PermissionAppName,
  PermissionCategoriesNames,
  PermissionEntitiesNames,
} from '../types';
import { ScreenType, ScreenTypeType } from '../types/savedViews.types';
import { EntityProjectionName, entityProjectionNames } from './entityProjectionNames';
import { alertsNoDataFound } from './ticketsEmptyStates/AlertsNoDataFound';
import { policyViolationsNoDataFound } from './ticketsEmptyStates/PolicyViolationsNoDataFound';
import { violationTicketsNoDataFound } from './ticketsEmptyStates/ViolationTicketsNoDataFound';

export const ACTIONS_MAP = {
  UPDATE: 'update',
  MERGE: 'merge',
  COMMENT: 'comment',
  OUTEGRATION_CREATE: 'outegration_create',
  OUTEGRATION_SCAN: 'outegration_scan',
  ALERT_TO_INCIDENT: 'alert_to_incident',
  ATTACHMENTS: 'attachments',
  OUTEGRATION_ACT: 'outegration_act',
  EXCEPTIONS_SECTION: 'exceptions-section',
  EXCEPTIONS_FORM: 'exceptions-form',
  SELECT_ALL: 'select-all',
  COPILOT: 'copilot',
  TRIGGER_PLAYBOOK: 'trigger-playbook',
};

type ActionsMapType = (typeof ACTIONS_MAP)[keyof typeof ACTIONS_MAP];

export const getActionsPermissionConfig = (
  projectionName: string,
  app?: string
): Record<
  string,
  {
    resource: PermissionEntitiesNames;
    category?: PermissionCategoriesNames;
    permission?: Permission;
  }
> => ({
  [ACTIONS_MAP.UPDATE]:
    projectionName === entityProjectionNames.Exception
      ? {
          category: PermissionAppName.VULNERABILITIES,
          resource: PermissionEntitiesNames.EXCEPTION,
          permission: Permission.UPDATE,
        }
      : { resource: PermissionEntitiesNames.OPT, permission: Permission.UPDATE },
  [ACTIONS_MAP.MERGE]: { resource: PermissionEntitiesNames.OPT, permission: Permission.CREATE },
  [ACTIONS_MAP.COMMENT]:
    projectionName === entityProjectionNames.Exception
      ? {
          category: PermissionAppName.VULNERABILITIES,
          resource: PermissionEntitiesNames.EXCEPTION,
          permission: Permission.UPDATE,
        }
      : { resource: PermissionEntitiesNames.OPT, permission: Permission.UPDATE },
  [ACTIONS_MAP.OUTEGRATION_CREATE]:
    projectionName === entityProjectionNames.Exception
      ? {
          category: PermissionAppName.VULNERABILITIES,
          resource: PermissionEntitiesNames.EXCEPTION,
          permission: Permission.CREATE,
        }
      : { resource: PermissionEntitiesNames.INT, permission: Permission.CREATE },

  [ACTIONS_MAP.OUTEGRATION_SCAN]: { resource: PermissionEntitiesNames.INT, permission: Permission.CREATE },
  [ACTIONS_MAP.ALERT_TO_INCIDENT]: { resource: PermissionEntitiesNames.OPT, permission: Permission.UPDATE },
  [ACTIONS_MAP.ATTACHMENTS]:
    projectionName === entityProjectionNames.Exception
      ? {
          category: PermissionAppName.VULNERABILITIES,
          resource: PermissionEntitiesNames.EXCEPTION,
          permission: Permission.UPDATE,
        }
      : { resource: PermissionEntitiesNames.OPT, permission: Permission.UPDATE },
  [ACTIONS_MAP.OUTEGRATION_ACT]: { resource: PermissionEntitiesNames.INT, permission: Permission.CREATE },
  [ACTIONS_MAP.EXCEPTIONS_SECTION]: {
    category: (app as PermissionCategoriesNames) || PermissionAppName.VULNERABILITIES,
    resource: PermissionEntitiesNames.EXCEPTION,
    permission: Permission.READ,
  },
  [ACTIONS_MAP.EXCEPTIONS_FORM]: {
    category: (app as PermissionCategoriesNames) || PermissionAppName.VULNERABILITIES,
    resource: PermissionEntitiesNames.EXCEPTION,
    permission: Permission.CREATE,
  },
  [ACTIONS_MAP.COPILOT]: { resource: PermissionEntitiesNames.OPT, permission: Permission.UPDATE },
});

export type EntityConfigType = {
  path: PagePathsValue;
  entityTypeId: string;
  projectionName: EntityProjectionName;
  pageTitle: string;
  appPath: AppPathsValue;
  app: AppName;
  viewScreenType: ScreenTypeType;
  searchConfig: {
    contextKey: SearchContextKeys;
    placeholder?: string;
    mode?: TicketsSearchMode;
    fields?: string[];
  };
  defaultSortBy: (alias: string, isNewRiskScoreEnabled?: boolean) => { property: string; isAsc: boolean }[];
  titleFieldName?: string | string[];
  labelsV2?: Record<string, string>;
  defaultFilters?: Record<string, any[]> | ((featureFlags?: FeatureFlagMap) => Record<string, any[]>);
  showVisualEntityExplorer?: boolean;
  defaultSavedView: string;
  vulnsField?: string;
  extraFilters?: string[];
  hideCommentsTab?: boolean;
  hideActivityTab?: boolean;
  hideGroupBy?: boolean;
  hideApplyChanges?: boolean;
  groupingKeyField?: string;
  extraRelationGroups?: string[];
  rowGroupByField?: (row: any) => any;
  getHeadCellFunc?: (hc: HeadCellType[] | TColumnsDef, isTableV2?: boolean, featureFlags?: FeatureFlagMap) => HeadCellType[] | TColumnsDef;
  getData?: (hc: any) => any;
  getBaseSql?: (params: BaseSqlParams) => string;
  getDetailSql?: (params: Omit<TicketDetailSqlParams, 'titleField'>) => string;
  findingsCountField?: string;
  groupingExplanationConfig?: { url: string; prefix: string; suffix?: string };
  ChartsComponent?: React.ComponentType<IEntityViewChartsProps>;
  actions: {
    grid?: Record<ActionsMapType, boolean>;
    panel?: Record<ActionsMapType, boolean>;
  };
  styles?: {
    isTransparentPageBackground?: boolean;
    tableSize?: TableSizeEnum;
  };
  NoDataComponent?: React.ComponentType<{
    projDisplayName: string;
    isRefetching: boolean;
    clearAllFilters: (keepView?: boolean, keepHeadCells?: boolean) => void;
  }>;
  isMultiSelect?: boolean;
  disablePagination?: boolean;
  currentStatusName?: typeof CURRENT_STATUS_FIELD | typeof NESTED_CURRENT_STATUS_FIELD;
  filterBucketsFunc?: (bucket: BucketType, currentActiveBucket?: BucketType) => boolean;
  showAllFieldsToggle?: boolean;
  vulCountBySeveritySummaryConfig?: VulCountBySeveritySummaryConfig;
  alertCountBySeveritySummaryConfig?: VulCountBySeveritySummaryConfig;
  userCountBySeveritySummaryConfig?: VulCountBySeveritySummaryConfig;
  configurationBanner?: Omit<ConfigurationBannerProps, 'queryKey'> & {
    ffForShowing?: FeatureFlagValue;
    permission?: PermissionToResourceType;
  };
  showDiscoveryChain?: boolean;
  showAllFieldsAsList?: boolean;
};

type IEntityViewConfig = {
  [key in EntityTypeConfigKeys as (typeof entityProjectionNames)[key]]: EntityConfigType;
};

export enum EntityTypeConfigKeys {
  Ticket = 'Ticket',
  Asset = 'Asset',
  Incident = 'Incident',
  Alert = 'Alert',
  Finding = 'Finding',
  PolicyViolation = 'PolicyViolation',
  Exception = 'Exception',
  ViolationTicket = 'ViolationTicket',
  IdentityFinding = 'IdentityFinding',
  User = 'User',
  Identity = 'Identity',
  Tenant = 'Tenant',
}

export type TicketsSearchMode = 'DEFAULT' | 'FULL_WIDTH';

export type EntityViewContext = {
  appName?: AppPathsValue;
};

const entityViewConfigAppOverrides: Partial<Record<EntityProjectionName, Partial<Record<AppPathsValue, Partial<EntityConfigType>>>>> = {
  [entityProjectionNames.Exception]: {
    [APP_PATHS.IDENTITIES]: {
      pageTitle: 'Exceptions',
      path: PAGE_PATHS.EXCEPTIONS,
      entityTypeId: 'Exception',
      projectionName: entityProjectionNames.Exception,
      app: appNames.Identities,
      appPath: APP_PATHS.IDENTITIES,
      ChartsComponent: ExceptionsIdnpViewCharts,
      viewScreenType: ScreenType.ExceptionsIdnpView,
      getBaseSql: baseExceptionsSql,
      getDetailSql: getExceptionsIdnpSql,
      defaultSavedView: undefined,
      searchConfig: {
        contextKey: SearchContextKeys.Exceptions,
        fields: ['identity_finding.title', 'exception._key'],
      },
      groupingKeyField: 'identity_finding._key',
      extraFilters: ["exception.state != 'ARCHIVED'"],
      defaultSortBy: () => [{ property: 'exception._key', isAsc: true }],
      defaultFilters: {
        'exception.current_status': [],
        'identity_finding.severity': [],
        'identity_finding.title': [],
        'requester.requester_email': [],
      },
      actions: {
        grid: {
          [ACTIONS_MAP.UPDATE]: true,
          [ACTIONS_MAP.COMMENT]: true,
          [ACTIONS_MAP.OUTEGRATION_CREATE]: true,
          [ACTIONS_MAP.SELECT_ALL]: true,
        },
        panel: { [ACTIONS_MAP.OUTEGRATION_CREATE]: true, [ACTIONS_MAP.ATTACHMENTS]: true },
      },
      styles: {
        isTransparentPageBackground: true,
      },
      NoDataComponent: undefined,
      currentStatusName: CURRENT_STATUS_FIELD,
      filterBucketsFunc: (bucket, currentActiveBucket) => (currentActiveBucket?.approved ? false : !bucket.closed && !bucket.approved),
    },
  },
  [entityProjectionNames.Alert]: {
    [APP_PATHS.IDENTITIES]: {
      app: appNames.Identities,
      appPath: APP_PATHS.IDENTITIES,
    },
  },
};

export const entityViewConfig: IEntityViewConfig = {
  [entityProjectionNames.Ticket]: {
    path: PAGE_PATHS.TICKETS,
    entityTypeId: 'Ticket',
    projectionName: entityProjectionNames.Ticket,
    pageTitle: 'Tickets',
    appPath: APP_PATHS.VULNERABILITIES,
    app: appNames.Vulnerabilities,
    viewScreenType: ScreenType.Tickets,
    getBaseSql: baseTicketSql,
    getDetailSql: params => getTicketSqlV2({ ...params, titleField: resolveQueryTitleField('ticket', 'title') }),
    labelsV2: FilterTypesLabelV2,
    showVisualEntityExplorer: true,
    searchConfig: {
      placeholder: 'Search Title',
      fields: entitySearchFields[entityProjectionNames.Ticket],
      contextKey: SearchContextKeys.Tickets,
    },
    defaultSavedView: 'Active',
    vulnsField: 'ticket.finding_count',
    extraFilters: ["ticket.state != 'ARCHIVED'"],
    defaultFilters: {
      'ticket.is_fixable': [],
      'ticket.current_status.name': [],
      'ticket.severity': [],
      'ticket.source_names': [],
    },
    groupingKeyField: 'finding.grouping_key',
    titleFieldName: 'title',
    extraRelationGroups: ['Assignee'],
    defaultSortBy: (alias, isNewRiskScoreEnabled) => defaultSortByTickets(alias, isNewRiskScoreEnabled),
    actions: {
      grid: {
        [ACTIONS_MAP.UPDATE]: true,
        [ACTIONS_MAP.MERGE]: true,
        [ACTIONS_MAP.COMMENT]: true,
        [ACTIONS_MAP.OUTEGRATION_CREATE]: true,
        [ACTIONS_MAP.OUTEGRATION_ACT]: true,
        [ACTIONS_MAP.SELECT_ALL]: true,
      },
      panel: {
        [ACTIONS_MAP.ATTACHMENTS]: true,
        [ACTIONS_MAP.EXCEPTIONS_SECTION]: true,
        [ACTIONS_MAP.EXCEPTIONS_FORM]: true,
        [ACTIONS_MAP.COPILOT]: true,
      },
    },
    styles: {
      isTransparentPageBackground: true,
    },
    NoDataComponent: TicketsNoDataFoundDefault,
    currentStatusName: NESTED_CURRENT_STATUS_FIELD,
    vulCountBySeveritySummaryConfig: {
      dims: RISK_MASS_DIMS,
      label: 'Risk Mass',
    },
    findingsCountField: 'allFindingCount',
    groupingExplanationConfig: {
      url: `${PAGE_PATHS.SETTINGS}/${PAGE_PATHS.RULE_SET}/`,
      prefix: 'Grouped by Finding Title as no grouping rules match this ticket.',
      suffix: 'to modify or add grouping rules to match these findings.',
    },
  },
  [entityProjectionNames.Asset]: {
    path: PAGE_PATHS.ASSETS,
    entityTypeId: 'Asset',
    projectionName: entityProjectionNames.Asset,
    ChartsComponent: AssetsViewCharts,
    pageTitle: 'Assets',
    appPath: APP_PATHS.VULNERABILITIES,
    app: appNames.Vulnerabilities,
    viewScreenType: ScreenType.AssetView,
    getBaseSql: baseAssetSql,
    getDetailSql: params => getAssetsSqlV2({ ...params, titleField: resolveQueryTitleField('asset', 'name') }),
    showVisualEntityExplorer: true,
    labelsV2: FilterTypesLabelV2,
    defaultFilters: {
      'asset.source_names': [],
      'asset.type': [],
      'asset.site': [],
      'asset.os': [],
    },
    searchConfig: {
      fields: entitySearchFields[entityProjectionNames.Asset],
      contextKey: SearchContextKeys.Assets,
    },
    defaultSavedView: 'Active',
    vulnsField: '',
    extraFilters: ["asset.state != 'ARCHIVED'"],
    hideCommentsTab: false,
    showAllFieldsToggle: true,
    extraRelationGroups: ['Owner', 'Operating System'],
    titleFieldName: 'name',
    defaultSortBy,
    actions: {
      grid: {
        [ACTIONS_MAP.UPDATE]: true,
        // Added to support Act button [AVA-9992] fixed in [AVA-10084]
        [ACTIONS_MAP.OUTEGRATION_ACT]: true,
        [ACTIONS_MAP.SELECT_ALL]: true,
        [ACTIONS_MAP.OUTEGRATION_SCAN]: true,
        [ACTIONS_MAP.COMMENT]: true,
      },
      panel: { [ACTIONS_MAP.ATTACHMENTS]: false },
    },
    styles: {
      isTransparentPageBackground: true,
    },
    NoDataComponent: TicketsNoDataFoundDefault,
    vulCountBySeveritySummaryConfig: {
      dims: RISK_MASS_DIMS,
      label: 'Risk Mass',
    },
    configurationBanner: {
      query: GET_DISCOVERY_SETTINGS_LIST,
      configureLink: { app: APP_PATHS.PLATFORM, path: PAGE_PATHS.EASM_SETTINGS },
      title: 'External Attack Surface Scanning',
      description: 'Scan and monitor your external attack surface by scanning your domains and integrating results into your inventory',
      ffForShowing: FeatureFlags.ShowAssetDiscoveryPage,
      permission: {
        resource: PermissionEntitiesNames.EASM_CONFIGURATION,
        permission: Permission.CREATE,
      },
      select: data => data.length > 0,
    },
    showDiscoveryChain: true,
    findingsCountField: 'total_findings_granular',
  },
  [entityProjectionNames.Incident]: {
    path: PAGE_PATHS.INCIDENTS,
    entityTypeId: 'Incident',
    projectionName: entityProjectionNames.Incident,
    ChartsComponent: IncidentsViewCharts,
    pageTitle: 'Incidents',
    app: appNames.Threats,
    appPath: APP_PATHS.THREATS,
    viewScreenType: ScreenType.IncidentsThreats,
    getBaseSql: baseIncidentSql,
    getDetailSql: params => getIncidentSqlV2({ ...params, titleField: resolveQueryTitleField('incident', 'title') }),
    labelsV2: FilterIncidentEntityTypesLabelV2,
    showVisualEntityExplorer: true,
    searchConfig: {
      fields: entitySearchFields[entityProjectionNames.Incident],
      contextKey: SearchContextKeys.Incidents,
    },
    defaultSavedView: 'Open Tickets',
    titleFieldName: 'title',
    vulnsField: 'incident.alert_count',
    extraFilters: ["incident.state NOT IN ('INACTIVE', 'ARCHIVED')"],
    defaultFilters: {
      'incident.current_status.name': [],
      'incident.severity': [],
      'incident.source_names': [],
    },
    defaultSortBy: defaultIncidentThreatsSortBy,
    actions: {
      grid: {
        [ACTIONS_MAP.UPDATE]: true,
        [ACTIONS_MAP.MERGE]: true,
        [ACTIONS_MAP.COMMENT]: true,
        [ACTIONS_MAP.OUTEGRATION_CREATE]: true,
        [ACTIONS_MAP.OUTEGRATION_ACT]: true,
        [ACTIONS_MAP.SELECT_ALL]: true,
      },
      panel: { [ACTIONS_MAP.ATTACHMENTS]: true, [ACTIONS_MAP.TRIGGER_PLAYBOOK]: true },
    },
    styles: {
      isTransparentPageBackground: true,
    },
    NoDataComponent: TicketsNoDataFoundDefault,
    currentStatusName: NESTED_CURRENT_STATUS_FIELD,
    groupingExplanationConfig: {
      url: `${PAGE_PATHS.SETTINGS}/${PAGE_PATHS.INCIDENT_RULES}/edit/`,
      prefix: 'Grouped by rule',
    },
  },
  [entityProjectionNames.Alert]: {
    path: PAGE_PATHS.ALERTS,
    entityTypeId: 'Alert',
    projectionName: entityProjectionNames.Alert,
    ChartsComponent: AlertsViewCharts,
    pageTitle: 'Alerts',
    app: appNames.Threats,
    appPath: APP_PATHS.THREATS,
    viewScreenType: ScreenType.AlertsThreats,
    getBaseSql: baseAlertSql,
    getDetailSql: params => getAlertsSql({ ...params, titleField: resolveQueryTitleField('alert', 'title') }),
    showVisualEntityExplorer: true,
    labelsV2: FilterIncidentEntityTypesLabelV2,
    searchConfig: {
      fields: entitySearchFields[entityProjectionNames.Alert],
      contextKey: SearchContextKeys.AlertsNew,
    },
    defaultSavedView: 'Active',
    titleFieldName: 'title',
    defaultFilters: {
      'alert.state': [],
      'alert.first_seen': [],
      'mitre_attack.technique': [],
      'mitre_attack.tactic': [],
    },
    hideCommentsTab: true,
    hideActivityTab: true,
    vulnsField: '',
    defaultSortBy: defaultAlertsNewSortBy,
    actions: {
      grid: {
        [ACTIONS_MAP.UPDATE]: true,
        [ACTIONS_MAP.ALERT_TO_INCIDENT]: true,
        [ACTIONS_MAP.SELECT_ALL]: true,
        [ACTIONS_MAP.COMMENT]: true,
      },
      panel: {
        [ACTIONS_MAP.ATTACHMENTS]: false,
        [ACTIONS_MAP.TRIGGER_PLAYBOOK]: true,
      },
    },
    styles: {
      isTransparentPageBackground: false,
      tableSize: TableSizeEnum.Medium,
    },
    NoDataComponent: alertsNoDataFound,
    currentStatusName: CURRENT_STATUS_FIELD,
  },
  [entityProjectionNames.Finding]: {
    path: PAGE_PATHS.FINDINGS,
    entityTypeId: 'Finding',
    projectionName: entityProjectionNames.Finding,
    ChartsComponent: FindingsViewCharts,
    pageTitle: 'Findings',
    appPath: APP_PATHS.VULNERABILITIES,
    app: appNames.Vulnerabilities,
    viewScreenType: ScreenType.FindingsView,
    getBaseSql: baseFindingSql,
    getDetailSql: params => getFindingSqlV2({ ...params, titleField: resolveQueryTitleField('finding', 'title') }),
    showVisualEntityExplorer: true,
    labelsV2: FilterTypesLabelV2,
    searchConfig: {
      placeholder: 'Search for CVE or Keyword, Example: CVE-2021-44228...',
      fields: entitySearchFields[entityProjectionNames.Finding],
      mode: 'FULL_WIDTH',
      contextKey: SearchContextKeys.Findings,
    },
    defaultSavedView: 'Active Critical and High Findings',
    defaultFilters: {
      'finding.source_names': [],
      'finding.type': [],
      'finding.severity': [],
    },
    extraFilters: ["finding.state != 'ARCHIVED'"],
    extraRelationGroups: ['*'],
    titleFieldName: 'title',
    defaultSortBy,
    hideCommentsTab: false,
    actions: {
      grid: { [ACTIONS_MAP.UPDATE]: true, [ACTIONS_MAP.MERGE]: true, [ACTIONS_MAP.SELECT_ALL]: true },
      panel: { [ACTIONS_MAP.ATTACHMENTS]: false, [ACTIONS_MAP.COMMENT]: true },
    },
    styles: {
      isTransparentPageBackground: false,
      tableSize: TableSizeEnum.Medium,
    },
    NoDataComponent: TicketsNoDataFoundDefault,
  },
  [entityProjectionNames.PolicyViolation]: {
    path: PAGE_PATHS.POLICY_VIOLATIONS,
    entityTypeId: 'PolicyViolation',
    projectionName: entityProjectionNames.PolicyViolation,
    ChartsComponent: PolicyViolationCharts,
    pageTitle: 'Policy Violations',
    isMultiSelect: true,
    appPath: APP_PATHS.ASSETS,
    app: appNames.Assets,
    defaultSavedView: 'Active Violations',
    viewScreenType: ScreenType.PolicyViolations,
    getBaseSql: basePolicyViolationSql,
    getDetailSql: params => getPolicyViolationSql({ ...params, titleField: resolveQueryTitleField('policy_violation', 'title') }),
    labelsV2: FilterTypesLabelV2,
    searchConfig: {
      placeholder: 'Search Policy Violation',
      fields: entitySearchFields[entityProjectionNames.PolicyViolation],
      contextKey: SearchContextKeys.PolicyViolation,
    },
    defaultFilters: {
      'policy_violation.state': [],
      'policy_violation.severity': [],
      'policy_violation.policy_name': [],
    },
    disablePagination: false,
    extraRelationGroups: ['*'],
    titleFieldName: 'title',
    defaultSortBy,
    hideCommentsTab: true,
    hideActivityTab: true,
    hideGroupBy: false,
    actions: {
      grid: {
        [ACTIONS_MAP.UPDATE]: true,
        [ACTIONS_MAP.MERGE]: false,
        [ACTIONS_MAP.OUTEGRATION_CREATE]: true,
        [ACTIONS_MAP.OUTEGRATION_ACT]: true,
        [ACTIONS_MAP.SELECT_ALL]: true,
      },
      panel: { [ACTIONS_MAP.ATTACHMENTS]: false },
    },
    styles: {
      isTransparentPageBackground: false,
      tableSize: TableSizeEnum.Medium,
    },
    NoDataComponent: policyViolationsNoDataFound,
  },
  [entityProjectionNames.Exception]: {
    pageTitle: 'Exceptions',
    path: PAGE_PATHS.EXCEPTIONS,
    entityTypeId: 'Exception',
    projectionName: entityProjectionNames.Exception,
    ChartsComponent: ExceptionsViewCharts,
    app: appNames.Vulnerabilities,
    hideApplyChanges: true,
    appPath: APP_PATHS.VULNERABILITIES,
    viewScreenType: ScreenType.ExceptionsView,
    getBaseSql: baseExceptionsSql,
    getDetailSql: getExceptionsSql,
    defaultSavedView: 'Active',
    searchConfig: {
      contextKey: SearchContextKeys.Exceptions,
      fields: entitySearchFields[entityProjectionNames.Exception],
    },
    groupingKeyField: 'ticket._key',
    extraFilters: ["exception.state != 'ARCHIVED'"],
    defaultSortBy: defaultExceptionsSortBy,
    getHeadCellFunc: (headCells, isTableV2, featureFlags) => {
      const isAdvancedExceptionsEnabled = featureFlags?.[FeatureFlags.AdvancedExceptionsEnabled];
      if (isAdvancedExceptionsEnabled) {
        return headCells.map(hc => {
          if (hc.id === 'exception.requester_email' || hc.id === 'requester.name') {
            return isTableV2 ? { ...hc, header: 'Submitted By' } : { ...hc, label: 'Submitted By' };
          }
          if (hc.id === 'exception.requested_sla') {
            return isTableV2 ? { ...hc, header: 'Submitted Date' } : { ...hc, label: 'Submitted Date' };
          }
          return hc;
        });
      }
      return headCells;
    },
    defaultFilters: (featureFlags?: FeatureFlagMap) => {
      const isAdvancedExceptionsEnabled = featureFlags?.[FeatureFlags.AdvancedExceptionsEnabled];
      return {
        'exception.current_status': [],
        ...(isAdvancedExceptionsEnabled ? {} : { 'ticket.severity': [] }),
        'requester.name': [],
      };
    },
    actions: {
      grid: {
        [ACTIONS_MAP.UPDATE]: true,
        [ACTIONS_MAP.COMMENT]: true,
        [ACTIONS_MAP.OUTEGRATION_CREATE]: true,
        [ACTIONS_MAP.SELECT_ALL]: true,
      },
      panel: { [ACTIONS_MAP.OUTEGRATION_CREATE]: true, [ACTIONS_MAP.ATTACHMENTS]: true },
    },
    styles: {
      isTransparentPageBackground: true,
    },
    NoDataComponent: TicketsNoDataFoundDefault,
    currentStatusName: CURRENT_STATUS_FIELD,
    filterBucketsFunc: (bucket, currentActiveBucket) => (currentActiveBucket?.approved ? false : !bucket.closed && !bucket.approved),
  },
  [entityProjectionNames.ViolationTicket]: {
    path: PAGE_PATHS.VIOLATION_TICKETS,
    entityTypeId: 'ViolationTicket',
    projectionName: entityProjectionNames.ViolationTicket,
    pageTitle: 'Violation Tickets',
    isMultiSelect: true,
    appPath: APP_PATHS.ASSETS,
    app: appNames.Assets,
    defaultSavedView: 'Active',
    viewScreenType: ScreenType.ViolationTicket,
    getBaseSql: baseViolationTicketSql,
    getDetailSql: params => getViolationTicketsSql({ ...params, titleField: resolveQueryTitleField('violation_ticket', 'title') }),
    labelsV2: FilterTypesLabelV2,
    searchConfig: {
      placeholder: 'Search Violation Tickets',
      fields: entitySearchFields[entityProjectionNames.ViolationTicket],
      contextKey: SearchContextKeys.ViolationTicket,
    },
    defaultFilters: {
      'violation_ticket.severity': [],
      'violation_ticket.current_status': [],
    },
    disablePagination: false,
    extraRelationGroups: ['Assignee'],
    titleFieldName: 'title',
    defaultSortBy,
    hideCommentsTab: false,
    hideGroupBy: false,
    actions: {
      grid: {
        [ACTIONS_MAP.UPDATE]: true,
        [ACTIONS_MAP.MERGE]: true,
        [ACTIONS_MAP.COMMENT]: true,
        [ACTIONS_MAP.OUTEGRATION_CREATE]: true,
        [ACTIONS_MAP.OUTEGRATION_ACT]: true,
        [ACTIONS_MAP.SELECT_ALL]: true,
      },
      panel: {
        [ACTIONS_MAP.ATTACHMENTS]: true,
      },
    },
    styles: {
      isTransparentPageBackground: true,
    },
    NoDataComponent: violationTicketsNoDataFound,
    currentStatusName: CURRENT_STATUS_FIELD,
  },
  [entityProjectionNames.IdentityFinding]: {
    path: PAGE_PATHS.IDENTITY_FINDINGS,
    entityTypeId: 'IdentityFinding',
    projectionName: entityProjectionNames.IdentityFinding,
    ChartsComponent: IdentityFindingsViewCharts,
    pageTitle: 'Identity Findings',
    appPath: APP_PATHS.IDENTITIES,
    viewScreenType: ScreenType.IdentityFindingsView,
    getBaseSql: baseIdentityFindingsSql,
    getDetailSql: getIdentityFindingsSql,
    app: appNames.Identities,
    showVisualEntityExplorer: true,
    searchConfig: {
      fields: entitySearchFields[entityProjectionNames.IdentityFinding],
      mode: 'FULL_WIDTH',
      placeholder: 'Search for Identity Findings or Keywords, Example: Admin without MFA"',
      contextKey: SearchContextKeys.IdentityFindings,
    },
    defaultFilters: {
      'identity_finding.title': [],
      'tenant.name': [],
    },
    defaultSavedView: 'Active',
    vulnsField: '',
    hideCommentsTab: true,
    hideActivityTab: true,
    titleFieldName: 'title',
    defaultSortBy,
    actions: {
      panel: {
        [ACTIONS_MAP.TRIGGER_PLAYBOOK]: true,
        [ACTIONS_MAP.EXCEPTIONS_SECTION]: true,
        [ACTIONS_MAP.EXCEPTIONS_FORM]: true,
      },
    },
    styles: {
      isTransparentPageBackground: false,
      tableSize: TableSizeEnum.Medium,
    },
    NoDataComponent: TicketsNoDataFoundDefault,
    hideApplyChanges: true,
    isMultiSelect: false,
  },
  [entityProjectionNames.User]: {
    path: PAGE_PATHS.USERS,
    entityTypeId: 'User',
    projectionName: entityProjectionNames.User,
    ChartsComponent: IdentitiesIdentityViewCharts,
    viewScreenType: ScreenType.UsersView,
    getBaseSql: baseIdentitiesSql,
    getDetailSql: params => getIdentitiesSql({ ...params, titleField: resolveQueryTitleField('user', ['email', 'user_name']) }),
    pageTitle: 'Users',
    appPath: APP_PATHS.IDENTITIES,
    app: appNames.Identities,
    labelsV2: FilterTypesLabelV2,
    showVisualEntityExplorer: true,
    defaultFilters: {
      'user.user_name': [],
      'user.type': [],
      'user.identity_type': [],
      'user.source_names': [],
    },
    searchConfig: {
      fields: entitySearchFields[entityProjectionNames.User],
      contextKey: SearchContextKeys.Identities,
    },
    defaultSavedView: 'Active',
    vulnsField: '',
    hideCommentsTab: true,
    hideActivityTab: true,
    titleFieldName: ['email', 'user_name'],
    defaultSortBy: () => [
      {
        property: 'user.email',
        isAsc: true,
      },
    ],
    actions: {
      panel: { [ACTIONS_MAP.TRIGGER_PLAYBOOK]: true },
    },
    styles: {
      isTransparentPageBackground: true,
    },
    NoDataComponent: TicketsNoDataFoundDefault,
    userCountBySeveritySummaryConfig: {
      dims: {
        LOW: 'low_active_identity_findings',
        MEDIUM: 'medium_active_identity_findings',
        HIGH: 'high_active_identity_findings',
        CRITICAL: 'critical_active_identity_findings',
        TOTAL: 'total_identity_findings',
      },
      label: 'Identity Findings',
      tooltip: 'The rounded sum of severity scores of active identity findings',
    },
    alertCountBySeveritySummaryConfig: {
      dims: {
        LOW: 'low_active_alerts',
        MEDIUM: 'medium_active_alerts',
        HIGH: 'high_active_alerts',
        CRITICAL: 'critical_active_alerts',
        TOTAL: 'active_alerts_granular',
      },
      label: 'Alerts',
      tooltip: 'The count of active alerts by severity',
    },
    hideApplyChanges: true,
    isMultiSelect: false,
  },
  [entityProjectionNames.Tenant]: {
    path: PAGE_PATHS.TENANTS,
    entityTypeId: 'Tenant',
    projectionName: entityProjectionNames.Tenant,
    ChartsComponent: TenantsViewCharts,
    pageTitle: 'Tenants',
    appPath: APP_PATHS.IDENTITIES,
    viewScreenType: ScreenType.TenantsView,
    getBaseSql: baseTenantSql,
    getDetailSql: params => getTenantsSql({ ...params, titleField: resolveQueryTitleField('tenant', 'name') }),
    app: appNames.Identities,
    labelsV2: FilterTypesLabelV2,
    showVisualEntityExplorer: true,
    searchConfig: {
      fields: entitySearchFields[entityProjectionNames.Tenant],
      contextKey: SearchContextKeys.Tenants,
    },
    defaultSavedView: 'Active',
    vulnsField: '',
    hideCommentsTab: true,
    hideActivityTab: true,
    titleFieldName: 'name',
    defaultSortBy,
    defaultFilters: {
      'tenant.user_name': [],
      'tenant.source_names': [],
    },
    actions: {},
    styles: {
      isTransparentPageBackground: true,
    },
    NoDataComponent: TicketsNoDataFoundDefault,
    userCountBySeveritySummaryConfig: {
      dims: {
        LOW: 'low_active_identity_findings',
        MEDIUM: 'medium_active_identity_findings',
        HIGH: 'high_active_identity_findings',
        CRITICAL: 'critical_active_identity_findings',
        TOTAL: 'total_identity_findings',
      },
      label: 'Identity Findings',
      tooltip: 'The rounded sum of severity scores of active identity findings',
    },
    alertCountBySeveritySummaryConfig: {
      dims: {
        LOW: 'low_active_alerts',
        MEDIUM: 'medium_active_alerts',
        HIGH: 'high_active_alerts',
        CRITICAL: 'critical_active_alerts',
        TOTAL: 'active_alerts_granular',
      },
      label: 'Alerts',
      tooltip: 'The count of active alerts by severity',
    },
    hideApplyChanges: true,
    isMultiSelect: false,
  },
  [entityProjectionNames.Identity]: {
    path: PAGE_PATHS.IDENTITY,
    entityTypeId: 'Identity',
    projectionName: entityProjectionNames.Identity,
    viewScreenType: ScreenType.IdentityView,
    getBaseSql: baseIdentitySql,
    pageTitle: 'Identity',
    appPath: APP_PATHS.IDENTITIES,
    app: appNames.Identities,
    labelsV2: FilterTypesLabelV2,
    defaultFilters: {
      'identity.user_name': [],
      'identity.identity_type': [],
      'identity.source_names': [],
    },
    searchConfig: {
      fields: entitySearchFields[entityProjectionNames.Identity],
      contextKey: SearchContextKeys.Identities,
    },
    defaultSavedView: 'Active',
    showVisualEntityExplorer: true,
    vulnsField: '',
    hideCommentsTab: true,
    hideActivityTab: true,
    titleFieldName: ['user_name', 'email'],
    defaultSortBy: alias => [
      {
        property: `${alias}.user_name`,
        isAsc: true,
      },
    ],
    actions: {},
    styles: {
      isTransparentPageBackground: true,
    },
    NoDataComponent: TicketsNoDataFoundDefault,
    hideApplyChanges: true,
    isMultiSelect: false,
  },
};

export const getExtraRelationGroupsOverrides = (): Partial<Record<EntityProjectionName, string[]>> => ({
  [entityProjectionNames.Finding]: [
    'Ticket',
    'Assignee',
    'Owner',
    'Vulnerability',
    'Vulnerability EASM',
    'Asset',
    'Component',
    'Component Instance',
    'FindingEvidence',
    'Finding Evidence Output',
  ],
  [entityProjectionNames.PolicyViolation]: ['Asset', 'Assignee'],
});

export const resolveTitleField = (entityAlias: string, titleFieldName?: string | string[]): string | undefined => {
  const titleField = Array.isArray(titleFieldName) ? titleFieldName[0] : titleFieldName;
  return titleField ? `${entityAlias}.${titleField}` : undefined;
};

export const resolveQueryTitleField = (entityAlias: string, titleFieldName?: string | string[]): string => {
  if (Array.isArray(titleFieldName)) {
    return `COALESCE(${titleFieldName.map(f => `${entityAlias}.${f}`).join(', ')})`;
  }
  return `${entityAlias}.${titleFieldName}`;
};

export const generateEntityViewConfig = (
  key: EntityTypeConfigKeys | string | undefined,
  context: EntityViewContext = {}
): EntityConfigType | undefined => {
  if (!key) return undefined;
  const projectionName = (entityProjectionNames as Partial<Record<string, EntityProjectionName>>)[key] ?? (key as EntityProjectionName);
  const base = entityViewConfig[projectionName];
  if (!base) return undefined;
  const override = context.appName && entityViewConfigAppOverrides[projectionName]?.[context.appName];
  return override ? { ...base, ...override } : base;
};
